/**
 * Business Entity Context
 * Global filter for Consolidated | Australia | US across all domains
 * Persists selection to localStorage
 * Respects user entity access from RBAC
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { useAuth } from './AuthContext';

const BusinessEntityContext = createContext();

export const useBusinessEntity = () => {
  const context = useContext(BusinessEntityContext);
  if (!context) {
    throw new Error('useBusinessEntity must be used within a BusinessEntityProvider');
  }
  return context;
};

export const ENTITY_LABELS = {
  all: 'Consolidated',
  AU: 'Australia',
  US: 'United States'
};

export const BusinessEntityProvider = ({ children }) => {
  const { getAccessibleEntities, isAuthenticated } = useAuth();

  const accessibleEntities = useMemo(() => {
    if (!isAuthenticated) return ['all', 'AU', 'US'];
    return getAccessibleEntities();
  }, [isAuthenticated, getAccessibleEntities]);

  const [entity, setEntityState] = useState(() => {
    try {
      return localStorage.getItem('businessEntity') || 'all';
    } catch {
      return 'all';
    }
  });

  // Reset to first accessible entity if current selection is not allowed
  useEffect(() => {
    if (accessibleEntities.length > 0 && !accessibleEntities.includes(entity)) {
      const fallback = accessibleEntities[0];
      setEntityState(fallback);
      try {
        localStorage.setItem('businessEntity', fallback);
      } catch {
        // localStorage not available
      }
    }
  }, [accessibleEntities, entity]);

  const setEntity = useCallback((value) => {
    // Only allow setting to accessible entities
    if (accessibleEntities.includes(value)) {
      setEntityState(value);
      try {
        localStorage.setItem('businessEntity', value);
      } catch {
        // localStorage not available
      }
    }
  }, [accessibleEntities]);

  const entityLabel = ENTITY_LABELS[entity] || 'Consolidated';

  // Returns entity value for API calls, undefined if 'all' (consolidated)
  const getEntityParam = useCallback(() => {
    return entity && entity !== 'all' ? entity : undefined;
  }, [entity]);

  const value = {
    entity,
    setEntity,
    entityLabel,
    getEntityParam,
    accessibleEntities
  };

  return (
    <BusinessEntityContext.Provider value={value}>
      {children}
    </BusinessEntityContext.Provider>
  );
};
